/**
 * Safe navigation utilities to handle SecurityError in local file contexts
 */

/**
 * Detects if the app is running from a local file
 * @returns {boolean} True if running from local file
 */
export const isLocalFile = () => {
  if (typeof window === 'undefined') return false;
  
  return (
    window.location.protocol === 'file:' ||
    window.location.href.startsWith('file://') ||
    window.location.origin === 'null' ||
    window.location.origin === ''
  );
};

/**
 * Detects if the app is running in a secure context (HTTPS or localhost)
 * @returns {boolean} True if running in secure context
 */
export const isSecureContext = () => {
  if (typeof window === 'undefined') return false;
  
  return (
    window.isSecureContext ||
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.localhost')
  );
};

/**
 * Creates a safe navigation function that handles SecurityError
 * @param {Object} history - React Router history object
 * @returns {Function} Safe navigation function
 */
export const createSafeNavigator = (history) => {
  return (path, options = {}) => {
    try {
      // Check if we're in a local file context
      if (isLocalFile()) {
        console.warn('Running from local file, using fallback navigation to:', path);
        
        // For local files, use window.location.href as fallback
        if (options.replace) {
          window.location.replace(path);
        } else {
          window.location.href = path;
        }
        return;
      }
      
      // For secure contexts, use normal React Router navigation
      if (options.replace) {
        history.replace(path);
      } else {
        history.push(path);
      }
    } catch (error) {
      console.warn('Navigation error, using fallback:', error.message);
      
      // Fallback to window.location for any navigation errors
      if (options.replace) {
        window.location.replace(path);
      } else {
        window.location.href = path;
      }
    }
  };
};

/**
 * Safe wrapper for React Router's useHistory hook
 * @param {Object} history - React Router history object
 * @returns {Object} Safe history object with wrapped methods
 */
export const createSafeHistory = (history) => {
  const safePush = createSafeNavigator(history);
  const safeReplace = (path) => createSafeNavigator(history)(path, { replace: true });
  
  return {
    ...history,
    push: safePush,
    replace: safeReplace,
    // Keep other methods as-is
    go: history.go,
    goBack: history.goBack,
    goForward: history.goForward,
    block: history.block,
    listen: history.listen,
    location: history.location,
    action: history.action,
    length: history.length,
    index: history.index,
    entries: history.entries,
    createHref: history.createHref
  };
};

/**
 * Detects if History API is available and working
 * @returns {boolean} True if History API is available
 */
export const isHistoryAPIAvailable = () => {
  if (typeof window === 'undefined') return false;
  
  try {
    // Test if we can create a history state
    const testState = { test: true };
    const testUrl = window.location.href;
    
    // Try to push a test state (this will fail in local file contexts)
    window.history.pushState(testState, '', testUrl);
    
    // If we get here, History API is working
    return true;
  } catch (error) {
    // History API is not available or blocked
    console.warn('History API not available:', error.message);
    return false;
  }
};

/**
 * Creates a navigation handler that works in all contexts
 * @param {Object} history - React Router history object
 * @param {string} path - Path to navigate to
 * @param {Object} options - Navigation options
 */
export const safeNavigate = (history, path, options = {}) => {
  const navigator = createSafeNavigator(history);
  return navigator(path, options);
};

/**
 * Hook to get safe navigation functions
 * @param {Object} history - React Router history object
 * @returns {Object} Object with safe navigation functions
 */
export const useSafeNavigation = (history) => {
  return {
    push: (path) => safeNavigate(history, path),
    replace: (path) => safeNavigate(history, path, { replace: true }),
    isLocalFile: isLocalFile(),
    isSecureContext: isSecureContext(),
    isHistoryAPIAvailable: isHistoryAPIAvailable()
  };
};

/**
 * Global error handler for navigation SecurityErrors
 */
export const setupNavigationErrorHandler = () => {
  if (typeof window === 'undefined') return;

  // Override the global error handler to catch navigation SecurityErrors
  const originalErrorHandler = window.onerror;
  
  window.onerror = (message, source, lineno, colno, error) => {
    // Check if it's a History API SecurityError
    if (message && message.includes('Failed to execute \'pushState\' on \'History\'')) {
      console.warn('History API SecurityError caught and handled:', message);
      
      // Log to Sentry if available
      if (window.Sentry) {
        window.Sentry.captureException(new Error(message), {
          tags: {
            errorType: 'HISTORY_API_SECURITY_ERROR',
            source: source,
            line: lineno,
            column: colno,
            context: isLocalFile() ? 'LOCAL_FILE' : 'WEB_CONTEXT'
          },
          level: 'warning'
        });
      }
      
      // Suppress the error to prevent it from showing in console
      return true;
    }
    
    // Call original error handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    
    return false;
  };

  console.log('Navigation error handler initialized');
};
